// What each role can do on its turn. One action per turn.
//
// Every entry answers three questions the UI and the reducer both need:
// what it costs, what it does, and whether it is legal right now. The
// `apply` step returns a civ patch plus any side effects (cards added to
// the deck, techs learned, regions found) so the reducer can log and seed
// the deck in one place.

import {
  UNHAPPY_AT, LEVY, GRAIN, HUNT_DIE, BAND, REPEAT_STEP, HAGGLE_DIE, HAGGLE_BASE, PONDER_DIE,
  VILLAGE, TOWN, CITY, WARBAND_CAP, UNIT_KINDS, UNIT_ORDER, REINFORCE_COST, CULL_COST, PARLEY_PER_STRENGTH, PARLEY_HOLDS_ON, RIVAL, GLAD_WORKER_AT,
  PONDER_BASE,
} from './constants';
import { modsOf } from './mods';
import {
  rivalsNear, rivalById, wayOf, rivalGround,
} from './rivals';
import { rivalsOf } from './rivals';
import {
  unitsOf, unitById, kindOf, stepOrders, stepsFrom, foesNear,
} from './units';
import { roadLevel, canLayRoad, roadBonus } from './roads';
import {
  canStand, isTown, hexDistanceBetween, hexDistance, siteAt, TOWN_RADIUS, allSites,
} from './map';
import {
  groundAt, stepCost, workAt,
} from './terrain';
import {
  BUILDINGS, BUILDING_ORDER, cityOf, workable, hexWithRoom, annexable, count as cityCount, ANNEX_COST, REPAIR_COST, canBuildType,
  machineStands,
} from './city';
import {
  HALL_CARDS, hallOf, cardPrice, COUNSELORS, likingFor, pairUpFor, seatUsed, freeSeats, councilOf, waitingOf, COUNCIL_SEATS, isOld, buysOf, offerOf,
} from './hall';
import {
  PRINCIPLES, ASPECTS, NATURES, natureKey, knownOf, settledOf, publishedOf,
} from './lore';
import {
  MACHINE_IDS, machineById, machineForTier, onBench, canFeed, fittingProduct,
} from './machines';
import {
  PUBLISH_COST, HINT_COST, MIX_COST, CROWD,
} from './constants';

/** The extra happiness a `crowd` card costs at this population. */
export const crowdExtra = (civ) => Math.max(0, Math.floor(((civ?.population || 0) - CROWD.from) / CROWD.per));
import {
  TECHS, REGIONS, sitesOf, techReachable, techCost, GOALS, goalOf,
} from './cards';
import { canAfford, missingFor, housingOf } from './effects';

// modsOf lives in mods.js now (so city.js can read it); re-exported for
// everything that imports it from here.
export { modsOf };
const noNeg = (cost) => Object.fromEntries(Object.entries(cost).map(([k, n]) => [k, Math.max(0, n)]));

/** What a stage of the era's great work costs, after the tree's discounts. */
export const buildCost = (civ) => {
  const goal = GOALS[goalOf(civ)];
  const base = goal?.build?.cost || { goods: 2, coin: 2, workers: 2 };
  const m = modsOf(civ);
  return noNeg({
    ...base,
    ...(base.goods !== undefined ? { goods: base.goods + m.buildGoods } : {}),
    ...(base.coin !== undefined ? { coin: base.coin + m.buildCoin } : {}),
    ...(base.workers !== undefined ? { workers: base.workers + m.buildWorkers } : {}),
  });
};

// `cost` may be a function of the civ (techs change prices). `gain` is
// display-only text; `apply` is the truth. `repeat: (civ) => n` means the
// action can be taken up to n times in one turn (`params.times`) - and n
// is INFRASTRUCTURE: one more worker may hunt per field, trade per market,
// study per library. Matt: "limits on how many workers you can use for any
// given action based on some infrastructure that we've been slowly
// expanding." `dice` means the gain is rolled, not fixed.
export const MAX_TIMES = 6;
export const ROLE_ACTIONS = {
  // ---- THE HALL. Everyone else decides in numbers, space, or a tree; the
  // Hall decides in CARDS: a hand dealt by its workers (two each, at the
  // start of the day), one card to each of its three counselors, a market
  // of ideas, a cull, and the compass every card pushes. Cards cost goods
  // and coin, never workers (Matt, 2026-10-14). See docs/DESIGN.md "The Hall".
  culture: [
    {
      // Every card goes onto a counselor: it costs what the card costs
      // (goods, coin, food - never a worker) and does what the card does,
      // twisted if that seat suits it. One card per seat per day, so the
      // council is the limit on plays. See engine/hall.js for a seat.
      id: 'play',
      label: 'Play a card',
      again: true,
      // FREE TO PLAY (playtest 14): the whole price of a card is paid once,
      // on the shelf. A play costs the seat it takes, and nothing else.
      // Face-down plays went in playtest 7 ("see if we miss it").
      cost: {},
      gain: () => 'what the card says, and a bonus from a counselor who likes it',
      blurb: 'Put a card on a counselor. It does what it says; a counselor who likes it adds their bonus.',
      needsParam: 'card',
      apply: (civ, { card, to, counselor, split, village } = {}) => ({
        play: card, to, counselor, split, village, facedown: false,
      }),
      legal: (state) => (hallOf(state.civ)?.hand || []).length > 0 && freeSeats(state.civ).length > 0,
      why: 'Your hand is empty, or every counselor has taken a card today.',
      legalParam: (civ, card, params = {}) => (hallOf(civ)?.hand || []).includes(card)
        && councilOf(civ).some((c) => c.id === params.counselor)
        && !seatUsed(civ, params.counselor)
        // A card that brings people needs somewhere to put them (housing).
        && !((HALL_CARDS[card]?.effect?.population || 0) > 0 && civ.population >= housingOf(civ))
        // The Charter names a village: one that has grown enough and is not a town yet.
        && !(HALL_CARDS[card]?.charter && params.village && !villagesOf(civ).some((v) => v.id === params.village && !v.chartered && v.size >= VILLAGE.charterAtSize)),
      whyParam: 'That card is not in your hand, that counselor has already taken one today, the town is full (the Stewards need to build a House), or that village cannot be chartered.',
    },
    {
      // Thin the deck (playtest 7: "add and remove cards more easily"). The
      // Elder's face-down deed used to be the only way a card ever left.
      // Once a day and free (2026-10-14: the Hall's workers are its draw
      // now, so nothing of the Hall's costs a worker).
      id: 'cull',
      label: 'Trash a card',
      // A coin (playtest 13, Matt: "it seemed too easy to get rid of
      // grievances... you can just throw away a card every turn").
      cost: { ...CULL_COST },
      gain: () => '+1 Novelty; the card leaves your deck for good',
      blurb: 'Once a day, for a coin, a card in hand leaves the deck for good.',
      needsParam: 'card',
      apply: (civ, { card } = {}) => ({ cull: card }),
      legal: (state) => (hallOf(state.civ)?.hand || []).length > 0,
      why: 'Your hand is empty.',
      legalParam: (civ, card) => (hallOf(civ)?.hand || []).includes(card),
      whyParam: 'That card is not in your hand.',
    },
    {
      // THE BRONZE AGE'S NEW VERBS (one a role; DESIGN.md "The long game").
      // The Hall: a TREATY with a friendly people - their carts come twice as
      // full, and an ally never raids.
      id: 'treaty', label: 'Sign a treaty', cost: { coin: 4 },
      gain: () => 'an ally: carts twice as full, and no raids',
      blurb: 'From the Bronze Age: a friendly people becomes an ally. Their carts bring twice as much, and they will not send raiders.',
      needsParam: 'rival',
      apply: (civ, { rival } = {}) => ({ treaty: rival }),
      legal: (state) => (state.civ.age || 0) >= 1 && rivalsOf(state.civ).some((r) => r.met && r.stance === 'friendly' && !r.allied),
      why: 'From the Bronze Age, with a friendly people not yet allied.',
      legalParam: (civ, rival) => rivalsOf(civ).some((r) => r.id === rival && r.met && r.stance === 'friendly' && !r.allied),
      whyParam: 'Not a friendly people, or allied already.',
    },
    {
      // One idea a day may be taken up (HALL_BUYS_PER_DAY); The Elders
      // Speak and The Gathering grant more, for the day they are played.
      id: 'buy',
      label: 'Buy a card',
      // The Year of the Fair: ideas cost one more to take up.
      cost: (civ, { card } = {}) => {
        const p = card ? { ...cardPrice(card) } : { goods: 1 };
        const more = modsOf(civ).buyMore;
        if (more) { const k = p.coin != null && p.goods == null ? 'coin' : 'goods'; p[k] = (p[k] || 0) + more; }
        return p;
      },
      again: true,
      gain: () => 'a card for your deck',
      blurb: 'Buy one of the three cards on the shelf. It goes into your discard and comes round.',
      needsParam: 'card',
      apply: (civ, { card } = {}) => ({ buy: card }),
      legal: (state) => buysOf(state.civ) > 0 && (hallOf(state.civ)?.market || []).length > 0,
      why: 'No more buys today - play a card that grants one.',
      legalParam: (civ, card) => (hallOf(civ)?.market || []).includes(card),
      whyParam: 'That is not on offer.',
    },
    {
      // THE BURDEN (Nick, playtest 13): the fourth card on the shelf pays
      // you to take it. No buy needed; once a day; it is a grievance.
      id: 'burden', label: 'Take on the burden', cost: {},
      gain: (civ) => { const b = HALL_CARDS[hallOf(civ)?.burden]; return b ? `${Object.entries(b.bounty || {}).map(([k, n]) => `+${n} ${k}`).join(', ')} now; the card festers in your deck` : 'what the burden pays'; },
      blurb: 'A bad card that pays you to take it. It goes into your deck as a grievance: settle it, or trash it for a coin.',
      apply: () => ({ burden: true }),
      legal: (state) => Boolean(hallOf(state.civ)?.burden),
      why: 'Nothing is on offer for the taking.',
    },
    {
      id: 'seat', label: 'Give a chair', cost: {}, again: true,
      gain: () => 'a counselor from the door takes a chair',
      blurb: 'Someone is waiting at the door. Seat them, if there is a chair.',
      needsParam: 'counselor',
      apply: (civ, { counselor } = {}) => ({ seat: counselor }),
      legal: (state) => waitingOf(state.civ).length > 0 && councilOf(state.civ).length < COUNCIL_SEATS,
      why: 'Nobody at the door, or no chair free.',
      legalParam: (civ, counselor) => waitingOf(civ).includes(counselor) && councilOf(civ).length < COUNCIL_SEATS,
      whyParam: 'Not at the door, or no chair free.',
    },
    {
      id: 'choose', label: 'Give the chair', cost: {}, again: true,
      gain: () => 'one of the offered takes the empty chair',
      blurb: 'A chair is empty and two have been put forward. Pick one; they sit for a term of their own.',
      needsParam: 'counselor',
      apply: (civ, { counselor } = {}) => ({ choose: counselor }),
      legal: (state) => offerOf(state.civ).length > 0 && councilOf(state.civ).length < COUNCIL_SEATS,
      why: 'No chair is empty, or nobody has been put forward.',
      legalParam: (civ, counselor) => offerOf(civ).includes(counselor) && councilOf(civ).length < COUNCIL_SEATS,
      whyParam: 'Not put forward, or no chair free.',
    },
    {
      id: 'dismiss', label: 'Thank and dismiss', cost: {},
      gain: () => 'a chair falls empty',
      blurb: 'Show a counselor out. Tonight two are put forward for their chair. Once a day.',
      needsParam: 'counselor',
      apply: (civ, { counselor } = {}) => ({ dismiss: counselor }),
      legal: (state) => councilOf(state.civ).length > 0,
      why: 'The chairs are empty.',
      legalParam: (civ, counselor) => councilOf(civ).some((c) => c.id === counselor),
      whyParam: 'Not on the council.',
    },
  ],
  // ---- THE STEWARDS. Everyone else decides in numbers or space; this role
  // decides in ALLOCATION: every hand it is given goes into a building.
  // See docs/DESIGN.md "The Stewards".
  trade: [
    {
      id: 'work', label: 'Work a building', cost: { workers: 1 }, again: true,
      gain: () => 'what the building makes',
      blurb: 'Put a shift into a field, a workshop, or the market. Each building takes so many shifts a day.',
      needsParam: 'building',
      apply: (civ, { building } = {}) => ({ work: building }),
      legal: (state) => workable(state.civ).length > 0,
      why: 'Every building is staffed, or burned.',
      legalParam: (civ, building) => workable(civ).some((b) => b.id === building),
      whyParam: 'That building is full, burned, or not yours.',
    },
    {
      // As many a day as the shifts and the stores allow (playtest 13, Matt:
      // "there's no reason to stop the stewards from building as many times
      // as they want to... the limitation can just be the number of shifts").
      id: 'build', label: 'Build', again: true, cost: (civ, { type } = {}) => (type && BUILDINGS[type] ? { workers: 2, ...BUILDINGS[type].cost } : { workers: 2, goods: 1 }),
      gain: () => 'a new building in the city',
      blurb: 'Raise a field, a workshop, a market, a granary, a warehouse, a mill, a library, or one of the Scholars\' machines, wherever there is room.',
      needsParam: 'type',
      apply: (civ, { type } = {}) => ({ build: type }),
      legal: (state) => BUILDING_ORDER.some((t) => canBuildType(state.civ, t) && hexWithRoom(cityOf(state.civ), t)),
      why: 'No free plot left. Take in land first.',
      legalParam: (civ, type) => canBuildType(civ, type) && Boolean(hexWithRoom(cityOf(civ), type)),
      whyParam: 'No free plot for that (take in land), the town does not know how yet, or one already stands.',
    },
    {
      id: 'annex', label: 'Annex a hex', cost: { ...ANNEX_COST },
      gain: () => '+6 plots of room',
      blurb: 'Take in the next hex of land. More room to build, and more edge for the Frontier to hold.',
      needsParam: 'hex',
      apply: (civ, { hex } = {}) => ({ annex: hex }),
      legal: (state) => annexable(state.civ).length > 0,
      why: 'Nothing next to the city can be taken in.',
      legalParam: (civ, hex) => Boolean(hex) && annexable(civ).some(([q, r]) => q === hex.q && r === hex.r),
      whyParam: 'That hex cannot be taken in.',
    },
    {
      id: 'repair', label: 'Repair', cost: { ...REPAIR_COST }, again: true,
      gain: () => 'a burned building works again',
      needsParam: 'building',
      blurb: 'Rebuild a building the raiders burned.',
      apply: (civ, { building } = {}) => ({ repair: building }),
      legal: (state) => cityOf(state.civ).buildings.some((b) => b.burned),
      why: 'Nothing is burned.',
      legalParam: (civ, building) => cityOf(civ).buildings.some((b) => b.id === building && b.burned),
      whyParam: 'That is not burned.',
    },
    {
      // A CARAVAN GOES SOMEWHERE. It used to cost two shifts and return
      // goods from nowhere; now it visits a named village, brings back a
      // full load instead of that village's nightly trickle, and feeds it so
      // it grows. Matt, 2026-09-20: "we don't know to whom."
      id: 'caravan',
      label: 'Send a caravan',
      // Food goes out with it while the settlement can still grow (playtest 11).
      cost: (civ, { village } = {}) => {
        const v = villagesOf(civ).find((x) => x.id === village);
        const food = caravanFood(civ, v);
        return { workers: 2, coin: 1, ...(food ? { food } : {}) };
      },
      again: true,
      // One a day per market, and Roads, Sailing, the Astrolabe and the
      // Harbour each let one more go - the promise those made for a week.
      limit: (civ) => cityCount(civ, 'market') + modsOf(civ).caravanCap,
      limitText: 'markets',
      gain: () => 'a day of what that settlement makes, today - and, fed, a village grows a size',
      blurb: 'Out to one of the Frontier\'s settlements and back, loaded.',
      needsParam: 'village',
      apply: (civ, { village } = {}) => ({ visit: village }),
      legal: (state) => cityCount(state.civ, 'market') > 0
        && villagesOf(state.civ).some((v) => !v.damaged),
      why: 'A caravan needs a market to leave from and somewhere to go.',
      legalParam: (civ, village) => villagesOf(civ).some((v) => v.id === village && !v.damaged),
      whyParam: 'That settlement is wrecked, or is not there.',
    },
    {
      // A town's ONE choice: the standing order it fills every night - food,
      // goods or coin (DESIGN.md "Villages and towns": "three toggles").
      // The network panel's buttons have always asked for this; the action
      // itself was never written, so a tap did nothing (playtest 0PNew).
      // Free: it is a setting, not a day's work.
      id: 'order', label: 'Set a standing order', cost: {}, again: true,
      gain: () => 'what the town sends in each night',
      blurb: 'A town fills one standing order every night: food, goods or coin.',
      needsParam: 'village',
      apply: (civ, { village, kind } = {}) => ({ order: { village, kind } }),
      legal: (state) => villagesOf(state.civ).some((v) => v.chartered),
      why: 'Only a town takes a standing order.',
      legalParam: (civ, village, params = {}) => TOWN.orders.includes(params.kind)
        && villagesOf(civ).some((v) => v.id === village && v.chartered && v.order !== params.kind),
      whyParam: 'Not a town, or it already fills that order.',
    },
    {
      // A town becomes a CITY (CITY in constants.js): two shifts, goods and
      // coin, and two settlers out of the capital.
      id: 'raise-city', label: 'Raise a city', cost: { workers: 2, ...CITY.cost }, again: true,
      gain: () => `a city: grows to ${CITY.maxSize}, a focus of its choosing, a district - ${CITY.settlers} people leave the capital for it, and it eats ${CITY.upkeepFood} food a night`,
      blurb: `A town of ${CITY.fromSize} with ground of its own becomes a city: ${CITY.settlers} people leave the capital to settle it. It grows to ${CITY.maxSize}, makes its size in the focus you set, eats ${CITY.upkeepFood}, and throws ${CITY.dice} dice at raiders.`,
      needsParam: 'village',
      apply: (civ, { village } = {}) => ({ raiseCity: { village } }),
      legal: (state) => villagesOf(state.civ).some((v) => canRaiseCity(state.civ, v)),
      why: `A city needs a town of ${CITY.fromSize} with its own ground around it, and a capital of ${CITY.settlersFrom} to send settlers.`,
      legalParam: (civ, village) => canRaiseCity(civ, villagesOf(civ).find((v) => v.id === village)),
      whyParam: 'Not a town ready to be a city, or too crowded, or the capital is too small to spare settlers.',
    },
    {
      id: 'city-focus', label: 'Set a city\'s focus', cost: {}, again: true,
      gain: () => 'what the city makes each night',
      blurb: 'A city makes its size in one thing: food, goods, coin, knowledge - or it holds the ground (defence: two more dice at raiders).',
      needsParam: 'village',
      apply: (civ, { village, kind } = {}) => ({ cityFocus: { village, kind } }),
      legal: (state) => villagesOf(state.civ).some((v) => v.city),
      why: 'No city yet.',
      legalParam: (civ, village, params = {}) => CITY.focuses.includes(params.kind) && villagesOf(civ).some((v) => v.id === village && v.city && v.focus !== params.kind),
      whyParam: 'Not a city, or it already has that focus.',
    },
    {
      id: 'city-district', label: 'Build a district', cost: { workers: 2, ...CITY.districtCost }, again: true,
      gain: () => 'a market, a school or barracks',
      blurb: 'One district a city: a market (+1 coin a night), a school (+1 knowledge a night), or barracks (warbands trained there come one stronger).',
      needsParam: 'village',
      apply: (civ, { village, district } = {}) => ({ cityDistrict: { village, district } }),
      legal: (state) => villagesOf(state.civ).some((v) => v.city && !v.district),
      why: 'No city without a district.',
      legalParam: (civ, village, params = {}) => Boolean(CITY.districts[params.district]) && villagesOf(civ).some((v) => v.id === village && v.city && !v.district),
      whyParam: 'Not a city, or it has its district already.',
    },
    {
      // The Stewards: UPGRADE a workshop to a foundry or a field to an
      // orchard - a shift there makes one more.
      id: 'upgrade', label: 'Upgrade a building', cost: { workers: 2, goods: 3, coin: 2 }, again: true,
      gain: () => 'a shift there makes one more',
      blurb: 'From the Bronze Age: a workshop becomes a foundry, a field an orchard. Every shift worked there makes one more.',
      needsParam: 'building',
      apply: (civ, { building } = {}) => ({ upgrade: building }),
      legal: (state) => (state.civ.age || 0) >= 1 && cityOf(state.civ).buildings.some((b) => UPGRADES[b.type] && !b.upgraded && !b.burned),
      why: 'From the Bronze Age, with a workshop or field not yet upgraded.',
      legalParam: (civ, building) => cityOf(civ).buildings.some((b) => b.id === building && UPGRADES[b.type] && !b.upgraded && !b.burned),
      whyParam: 'Not a workshop or field, or upgraded already.',
    },
    {
      id: 'buy-grain', label: 'Buy grain', cost: { coin: GRAIN.coin }, repeat: (civ) => cityCount(civ, 'market'), capText: 'markets',
      gain: (civ) => `+${GRAIN.food + modsOf(civ).grainFood} food`,
      blurb: 'Buy food with coin.',
      apply: (civ) => ({ patch: { food: GRAIN.food + modsOf(civ).grainFood } }),
      legal: (state) => cityCount(state.civ, 'market') > 0,
      why: 'Buying grain takes a market.',
    },
    // Hire craftsmen (coin into goods, 2026-09-21) was DROPPED after
    // playtest 11 - Matt: "I don't really know thematically what hiring
    // somebody is. I think we just drop it." HIRE stays in constants for
    // old logs only.
    {
      // A FREE action with a positive payoff is not a choice, it is a
      // button you press every turn. Matt, 2026-09-20: "why would you ever
      // not haggle? It seems like it's just a free coin every time." So it
      // costs half a day like any other errand, and pays a die instead of
      // a certainty: worse than a market shift, and it needs no market.
      // THE STEWARDS TRAIN THE UNITS, which makes a unit the one thing in
      // this game that all three of those roles touch: the Scholars
      // discover what is possible, the Stewards pay for it, the Frontier
      // commands it. Matt, 2026-09-20: "the Frontier has to say, hey, we
      // really need another warband, and the Steward has to manage, can we
      // afford to train them?"
      id: 'train',
      label: 'Train a unit',
      cost: (civ, { kind } = {}) => ({ workers: 2, ...(UNIT_KINDS[kind]?.trainCost || UNIT_KINDS.warband.trainCost) }),
      again: true,
      gain: () => 'a unit at the muster point',
      blurb: 'Raise a warband, a scout or a pioneer. It appears at the city, or at any town.',
      needsParam: 'kind',
      apply: (civ, { kind, at } = {}) => ({ train: { kind, at } }),
      legal: (state) => UNIT_ORDER.some((k) => canTrain(state.civ, k)),
      why: 'Nothing can be trained yet.',
      legalParam: (civ, kind) => canTrain(civ, kind),
      whyParam: 'The town does not know how to raise that yet.',
    },
    {
      // A warband that came home light can be brought back up to strength
      // where it was trained.
      id: 'reinforce',
      label: 'Reinforce',
      cost: { workers: 1, ...REINFORCE_COST },
      again: true,
      gain: () => '+1 strength',
      blurb: 'Bring a warband standing at a muster point back up to strength.',
      needsParam: 'unit',
      apply: (civ, { unit } = {}) => ({ reinforce: { unit } }),
      legal: (state) => unitsOf(state.civ).some((u) => canReinforce(state.civ, u)),
      why: 'Bring a warband to the city or a town first - a unit is reinforced where it can muster.',
      legalParam: (civ, unit) => {
        const u = unitById(civ, unit);
        return Boolean(u) && canReinforce(civ, u);
      },
      whyParam: 'Not standing in the city or a town, or already at full strength.',
    },
    {
      id: 'haggle',
      label: 'Haggle',
      cost: { workers: 1 },
      dice: () => ({ faces: HAGGLE_DIE, base: HAGGLE_BASE, resource: 'coin', count: 1 }),
      gain: () => `${HAGGLE_BASE + 1}–${HAGGLE_BASE + HAGGLE_DIE} coin, on a die`,
      blurb: 'A shift with no building: a die of coin.',
      apply: () => ({}),
    },
  ],
  // ---- THE SCHOLARS. Everyone else decides in numbers, space, or cards;
  // the Scholars DEDUCE. Mix two principles and learn what they share; a
  // technology is a reaction to find; publish theories and let the night
  // judge them. See docs/DESIGN.md "The Scholars".
  science: [
    {
      id: 'mix',
      label: 'Experiment',
      // A worker (more for the second and third today), a good's worth of
      // ingredients, and, when a technology is named, the KNOWLEDGE to write
      // it down: 2 + tier, less under an Empire or an Assembly, more under
      // the Elders. Knowledge is what the Scholars spend again (2026-09-21).
      cost: (civ, params = {}, state) => ({
        ...(state ? mixCost(state) : { workers: 1 }),
        ...MIX_COST,
        ...(params.target && TECHS[params.target] ? { knowledge: techPrice(civ, params.target) } : {}),
      }),
      again: true,
      gain: () => 'what two principles share',
      blurb: 'Mix two principles. The result is the properties they agree on. Research a technology by naming it first: if the pair makes what it needs, it is learned.',
      needsParam: 'a',
      apply: (civ, { a, b, target } = {}) => ({ mix: { a, b, target } }),
      // Two different principles, and a technology the eight make (tier 1
      // and 2) if one is named. Above that, the machines.
      legalParam: (civ, a, params = {}) => PRINCIPLES.includes(a) && PRINCIPLES.includes(params.b) && a !== params.b
        && (!params.target || (techReachable(civ, params.target) && onBench(TECHS[params.target]?.tier))),
      whyParam: 'Two different principles, and a technology within reach that two of them could make.',
    },
    {
      // THE LADDER (2026-10-15): feed a machine two things from the rung
      // below, in order; one new thing comes out onto the shelf, and its
      // nature is shown. The machine's law decides it - see machines.js.
      id: 'fire',
      label: 'Fire',
      cost: (civ, params = {}, state) => ({
        ...(state ? mixCost(state) : { workers: 1 }),
        ...(machineById(params.machine)?.fuel || { goods: 1 }),
      }),
      again: true,
      gain: () => 'one new thing for the shelf, and what it is',
      blurb: 'Feed a machine two things, one after the other. What comes out follows from what went in, by the machine\'s law: work the law out and you can make what a technology asks for.',
      needsParam: 'machine',
      apply: (civ, { machine, a, b } = {}) => ({ fire: { machine, a, b } }),
      legal: (state) => MACHINE_IDS.some((m) => machineStands(state.civ, m)),
      why: 'No machine stands in the city yet. Learn Kilns, then ask the Stewards to build one.',
      legalParam: (civ, machine, params = {}) => MACHINE_IDS.includes(machine) && machineStands(civ, machine)
        && canFeed(civ, machine, params.a, params.b),
      whyParam: 'That machine is not standing, or the shelf does not hold those two.',
    },
    {
      // A deep technology is MADE from something on the shelf that has what
      // it needs: the thing is used up, and the knowledge pays for writing
      // it down, as a eureka always has.
      id: 'make',
      label: 'Make',
      cost: (civ, params = {}) => (params.target && TECHS[params.target] ? { knowledge: techPrice(civ, params.target) } : {}),
      again: true,
      gain: () => 'a technology',
      blurb: 'Use something on the shelf that has what a technology needs, and the technology is learned.',
      needsParam: 'target',
      apply: (civ, { target } = {}) => ({ make: { target } }),
      legal: (state) => Object.keys(TECHS).some((t) => makeable(state.civ, t)),
      why: 'Nothing on the shelf has what a technology within reach needs.',
      legalParam: (civ, target) => makeable(civ, target),
      whyParam: 'Nothing on the shelf has what that needs, or it is not within reach.',
    },
    {
      id: 'publish', label: 'Publish a theory', cost: { ...PUBLISH_COST },
      gain: () => 'if it holds: +2 knowledge and a student',
      blurb: 'Declare what a principle is. If the night proves it, it is settled knowledge, pays back double, and a student turns out at dawn for the role you named. If not, it is debunked and costs happiness.',
      needsParam: 'principle',
      apply: (civ, { principle, nature, teach } = {}) => ({ publish: { principle, nature, teach } }),
      legal: (state) => benchPrinciples(state.civ).some((p) => !settledOf(state.civ, p) && !publishedOf(state.civ, p)),
      why: 'Everything is settled or already published.',
      legalParam: (civ, principle, params = {}) => benchPrinciples(civ).includes(principle) && !settledOf(civ, principle) && !publishedOf(civ, principle)
        && Boolean(params.nature) && ASPECTS.every((k) => typeof params.nature[k] === 'boolean'),
      whyParam: 'Already settled or published, or the nature is incomplete.',
    },
    {
      id: 'hint', label: 'Consult the stones', cost: { ...HINT_COST },
      gain: () => 'one property, named outright',
      blurb: 'Spend knowledge to learn one thing you did not know about a principle.',
      needsParam: 'principle',
      apply: (civ, { principle } = {}) => ({ hint: principle }),
      legal: (state) => benchPrinciples(state.civ).some((p) => ASPECTS.some((k) => knownOf(state.civ, p)[k] === undefined)),
      why: 'Nothing left to learn this way.',
      legalParam: (civ, principle) => benchPrinciples(civ).includes(principle) && ASPECTS.some((k) => knownOf(civ, principle)[k] === undefined),
      whyParam: 'Everything about that one is known.',
    },
    {
      // The Scholars: a SURVEY - map the nearest far region from the city,
      // before anyone has walked there.
      id: 'survey', label: 'Survey', cost: { workers: 1, knowledge: 2 },
      gain: () => 'the nearest far region, mapped',
      blurb: 'From the Bronze Age: the nearest region out in the fog comes into sight, and the ground around it.',
      apply: () => ({ survey: true }),
      legal: (state) => (state.civ.age || 0) >= 1 && Boolean(surveyTarget(state.civ)),
      why: 'From the Bronze Age, with a region out in the fog to map.',
    },
    {
      id: 'study', label: 'Study', cost: { workers: 1 }, repeat: (civ) => 1 + civ.libraries + modsOf(civ).studyCap, capText: 'libraries',
      gain: (civ) => `+${studyKnowledge(civ)} knowledge`,
      blurb: 'Put someone to reading. Knowledge pays for theories and for consulting the stones.',
      apply: (civ) => ({ patch: { knowledge: studyKnowledge(civ) } }),
    },
    {
      // Free and always worth it is not a choice. Matt, 2026-09-20: "why
      // would you ever not just ponder every turn?" A worker like anything
      // else, and a die rather than a certainty - worse than Study on
      // average, and it needs no library, which is its whole argument.
      id: 'ponder',
      label: 'Ponder',
      cost: { workers: 1 },
      dice: (civ) => ({
        faces: PONDER_DIE, base: PONDER_BASE + modsOf(civ).ponder, resource: 'knowledge', count: 1,
      }),
      gain: (civ) => `${1 + PONDER_BASE + modsOf(civ).ponder}–${PONDER_DIE + PONDER_BASE + modsOf(civ).ponder} knowledge, on a die`,
      blurb: 'A die of knowledge. No library needed.',
      apply: () => ({}),
    },
  ],
  // ---- THE FRONTIER. Everyone else decides in numbers; this role decides in
  // SPACE, and it now does it with several pieces instead of one. A WORKER
  // IS ONE UNIT'S TURN: every action here costs one order, and the dawn
  // split decides how many units get to do anything today. See
  // engine/units.js and docs/DESIGN.md "the frontier, the network".
  military: [
    {
      id: 'move',
      label: 'Move',
      cost: (civ, params = {}) => {
        const u = params.unit ? unitById(civ, params.unit) : null;
        return { workers: u && params.to ? stepOrders(civ, u, params.to.q, params.to.r) : 1 };
      },
      again: true,
      gain: () => 'one step, and they see where they go',
      blurb: 'One order a step, two into rough ground. Scouts go anywhere for one.',
      needsParam: 'unit',
      apply: (civ, { unit, to } = {}) => ({ moveUnit: { unit, to } }),
      legal: (state) => unitsOf(state.civ).length > 0,
      why: 'You have no units. The Stewards train them.',
      legalParam: (civ, unit, params = {}) => {
        const u = unitById(civ, unit);
        return Boolean(u) && Boolean(params.to)
          && stepsFrom(civ, u).some(([q, r]) => q === params.to.q && r === params.to.r);
      },
      whyParam: 'That unit cannot get there.',
    },
    {
      // A fight is an EXCHANGE, not a verdict: both sides roll their
      // strength, both sides lose what the other scored, and you may walk
      // away and come back with a second warband.
      id: 'fight',
      label: 'Fight',
      cost: { workers: 1 },
      again: true,
      gain: () => 'an exchange - both sides lose strength',
      blurb: 'Both roll their strength in dice. Every hit takes one strength off the other side.',
      needsParam: 'unit',
      apply: (civ, { unit, foe } = {}) => ({ fightWith: { unit, foe } }),
      legal: (state) => unitsOf(state.civ).some((u) => kindOf(u).canAttack && foesNear(state.civ, u).length),
      why: 'Nothing of yours is standing next to anything to fight.',
      legalParam: (civ, unit, params = {}) => {
        const u = unitById(civ, unit);
        return Boolean(u) && kindOf(u).canAttack
          && foesNear(civ, u).some((f) => f.id === params.foe);
      },
      whyParam: 'Not within reach, or that unit does not fight.',
    },
    {
      // MERGE two warbands into one (Matt, after playtest 12: "you should be
      // able to merge warbands if you want to, but you shouldn't be able to
      // split them"). This warband joins one standing on its hex or beside
      // it; together they cannot go past a warband's cap.
      id: 'merge',
      label: 'Merge',
      cost: { workers: 1 },
      again: true,
      gain: () => 'one bigger warband',
      blurb: 'Fold this warband into another on its hex or beside it. Their strength adds up, to a warband\'s cap. There is no splitting them again.',
      needsParam: 'unit',
      apply: (civ, { unit, into } = {}) => ({ merge: { unit, into } }),
      legal: (state) => unitsOf(state.civ).some((u) => mergeTargets(state.civ, u).length),
      why: 'No two warbands are side by side with room to join.',
      legalParam: (civ, unit, params = {}) => {
        const u = unitById(civ, unit);
        return Boolean(u) && mergeTargets(civ, u).some((t) => t.id === params.into);
      },
      whyParam: 'Not beside that warband, or together they would be too big.',
    },
    {
      // PAY THEM TO GO. Coin per point of the camp's strength; Diplomacy
      // halves it. A coin sink with a map on it, and the peaceable answer to
      // a camp too big to fight.
      id: 'parley',
      label: 'Parley',
      cost: (civ, params = {}) => ({ workers: 1, coin: parleyCost(civ, params) }),
      again: true,
      gain: () => `on a ${PARLEY_HOLDS_ON}+: the camp breaks up, or a rival warms to you`,
      blurb: `Coin to a camp beside you, or to a neighbouring people, and roll: on a ${PARLEY_HOLDS_ON} or better the camp breaks up or their temper improves; under it they keep the coin and nothing changes.`,
      needsParam: 'unit',
      // The coin paid rides along, so a refusal can hand it back (playtest 14).
      apply: (civ, { unit, foe } = {}) => ({ parley: { unit, foe, paid: { coin: parleyCost(civ, { unit, foe }) } } }),
      legal: (state) => unitsOf(state.civ).some((u) => foesNear(state.civ, u).some((f) => f.foe === 'camp') || rivalsNear(state.civ, u).length),
      why: 'Nothing of yours is standing beside a camp or a neighbouring people.',
      legalParam: (civ, unit, params = {}) => {
        const u = unitById(civ, unit);
        return Boolean(u) && (foesNear(civ, u).some((f) => f.foe === 'camp' && f.id === params.foe)
          || rivalsNear(civ, u).some((r) => r.id === params.foe));
      },
      whyParam: 'Not beside that.',
    },
    {
      // RAID a neighbouring people: an exchange against their power. Hits
      // take their power down; at nothing they are broken - their land is
      // free ground again and there is loot. Any raid makes them hostile.
      id: 'raid',
      label: 'Raid',
      cost: { workers: 1 },
      again: true,
      gain: () => 'an exchange against their strength; break them for loot',
      blurb: 'Fight a neighbouring people from beside their ground. They turn hostile.',
      needsParam: 'unit',
      apply: (civ, { unit, foe } = {}) => ({ raid: { unit, foe } }),
      legal: (state) => unitsOf(state.civ).some((u) => kindOf(u).canAttack && rivalsNear(state.civ, u).length),
      why: 'No warband of yours is standing beside a neighbouring people\'s ground.',
      legalParam: (civ, unit, params = {}) => {
        const u = unitById(civ, unit);
        return Boolean(u) && kindOf(u).canAttack && rivalsNear(civ, u).some((r) => r.id === params.foe);
      },
      whyParam: 'Not beside their ground, or that unit does not fight.',
    },
    {
      // A pioneer is spent doing this. What the village makes is what the
      // GROUND makes, so where you plant it is the whole decision.
      id: 'found',
      label: 'Found a village',
      cost: { workers: 1 },
      again: true,
      gain: (civ) => {
        const w = villageYieldAt(civ);
        return w ? `a village making ${w.resource}` : 'a village';
      },
      blurb: 'The pioneer settles here and is spent. The village sends up whatever this ground makes.',
      needsParam: 'unit',
      apply: (civ, { unit } = {}) => ({ foundVillage: { unit } }),
      legal: (state) => unitsOf(state.civ).some((u) => kindOf(u).canFound && canFoundAt(state.civ, u.q, u.r)),
      why: 'No pioneer is standing anywhere a village could go.',
      legalParam: (civ, unit) => {
        const u = unitById(civ, unit);
        return Boolean(u) && kindOf(u).canFound && canFoundAt(civ, u.q, u.r);
      },
      whyParam: 'Too near the city or another village, or the ground will not take one.',
    },
    {
      // The Frontier: a FORT on a hex - raiders cannot pass it, as if a
      // warband stood there for ever.
      id: 'fort', label: 'Build a fort', cost: { workers: 1, goods: 2 }, again: true,
      gain: () => 'raiders are held here, for good',
      blurb: 'From the Bronze Age: a warband raises a fort where it stands. Raiders cannot walk through it.',
      needsParam: 'unit',
      apply: (civ, { unit } = {}) => ({ fort: unit }),
      legal: (state) => (state.civ.age || 0) >= 1 && unitsOf(state.civ).some((u) => canFortAt(state.civ, u)),
      why: 'From the Bronze Age, with a warband out on open ground.',
      legalParam: (civ, unit) => canFortAt(civ, unitById(civ, unit)),
      whyParam: 'Not a warband, or not on open ground with no fort.',
    },
    {
      // Roads (Matt, 2026-09-25): laid a hex at a time, out from the city.
      id: 'road',
      label: 'Lay road',
      cost: { workers: 1, goods: 1 },
      again: true,
      gain: (civ) => `road here - ${roadLevel(civ) + 1} hexes an action along it`,
      blurb: 'Road on this hex: outside the city wall, beside road already laid, or at one of your villages. Units go further along road, and a village joined to the city by a road you can see sends more.',
      needsParam: 'unit',
      apply: (civ, { unit } = {}) => ({ layRoad: { unit } }),
      legal: (state) => roadLevel(state.civ) > 0 && unitsOf(state.civ).some((u) => canLayRoad(state.civ, u.q, u.r)),
      why: 'Needs Paths, and a unit standing just outside the city wall, at the end of a road, or at one of your villages.',
      legalParam: (civ, unit) => {
        const u = unitById(civ, unit);
        return Boolean(u) && canLayRoad(civ, u.q, u.r);
      },
      whyParam: 'Road must start outside the city wall, at road already laid, or at one of your villages, and not be there already.',
    },
    {
      id: 'repair-village',
      label: 'Repair',
      cost: { workers: 1 },
      again: true,
      gain: () => 'it produces again',
      blurb: 'A raided village makes nothing until it is repaired.',
      needsParam: 'unit',
      apply: (civ, { unit } = {}) => ({ repairVillage: { unit } }),
      legal: (state) => unitsOf(state.civ).some((u) => damagedAt(state.civ, u.q, u.r)),
      why: 'Nothing of yours is standing on a damaged village.',
      legalParam: (civ, unit) => {
        const u = unitById(civ, unit);
        return Boolean(u) && Boolean(damagedAt(civ, u.q, u.r));
      },
      whyParam: 'That unit is not standing on a damaged village.',
    },
    {
      // No units is no reason to sit the day out (Matt, playtest 9: "it'd be
      // nice if the Frontier could do something if we can't get them a
      // unit... otherwise I just get skipped, which is no fun"; Brian: "maybe
      // the Frontier could spend time getting goods somehow, with the
      // rolling"). Food, because food was the scarce thing all night.
      id: 'hunt-near',
      label: 'Hunt near the walls',
      cost: { workers: 1 },
      again: true,
      dice: (civ) => ({ faces: HUNT_DIE.faces, base: huntBase(civ), resource: 'food', count: 1 }),
      gain: (civ) => `${huntBase(civ) + 1}-${huntBase(civ) + HUNT_DIE.faces} food, on a die`,
      blurb: 'With no units out, the town hunts the near woods.',
      apply: () => ({}),
      legal: (state) => unitsOf(state.civ).length === 0,
      why: 'Only while the Frontier has no units out.',
    },
    {
      // HIRE A GUIDE (playtest 14, the Frontier twice: "trade goods or coin
      // for a worker"). Two coin for one order today, once a day: the one
      // coin-to-action trade in the game, at par (an order is a shift).
      id: 'hire',
      label: 'Hire a guide',
      cost: { coin: 2 },
      gain: () => '+1 order today',
      blurb: 'Two coin buys one more order for today. Once a day.',
      apply: () => ({ hire: 1 }),
      legal: (state) => state.meta?.phase === 'day',
      why: 'Only during the day.',
    },
    {
      // The ground still matters: a unit standing on workable land can spend
      // an order bringing back what is there.
      id: 'forage',
      label: (civ) => workHere(civ)?.label || 'Work the land',
      cost: { workers: 1 },
      again: true,
      // Paid by the ground THAT unit is standing on, so the die depends on
      // the params as well as the civ.
      dice: (civ, params = {}) => {
        const u = params.unit ? unitById(civ, params.unit) : null;
        const w = workHere(civ, u) || { faces: HUNT_DIE.faces, base: 0, resource: 'food' };
        return {
          faces: w.faces, base: w.base + huntBase(civ), resource: w.resource, count: huntDice(civ),
        };
      },
      gain: (civ) => {
        const w = workHere(civ);
        if (!w) return 'nothing here';
        const n = huntDice(civ);
        const lo = (w.base + huntBase(civ) + 1) * n;
        return `${lo}-${(w.base + huntBase(civ) + w.faces) * n} ${w.resource}, on ${n === 1 ? 'a die' : `${n} dice`}`;
      },
      blurb: 'A die of whatever this ground gives, brought back to the town.',
      needsParam: 'unit',
      apply: (civ, { unit } = {}) => ({ forage: { unit } }),
      legal: (state) => unitsOf(state.civ).some((u) => workAt(state.civ, u.q, u.r)),
      why: 'Nothing of yours is standing on ground worth working.',
      legalParam: (civ, unit) => {
        const u = unitById(civ, unit);
        return Boolean(u) && Boolean(workAt(civ, u.q, u.r));
      },
      whyParam: 'Nothing to work where that unit is standing.',
    },
  ],
};

// The shared action: once the era has a BUILD goal (the monument, the
// wall), ANY role can spend its turn on a stage of it.
export const MONUMENT_ACTION = {
  id: 'build-monument',
  label: (civ) => GOALS[goalOf(civ)]?.build?.label || 'Raise a stage',
  cost: (civ) => buildCost(civ),
  gain: (civ) => {
    const goal = GOALS[goalOf(civ)];
    const extra = goal?.build?.onStage ? `, ${Object.entries(goal.build.onStage).map(([k, n]) => `${n > 0 ? '+' : ''}${n} ${k}`).join(', ')}` : '';
    return `+1 stage${extra}`;
  },
  blurb: 'A stage of the wonder. When the last stands, it gives the town something for good.',
  apply: () => ({ monument: 1 }),
  legal: (state) => Boolean(GOALS[goalOf(state.civ)]?.build),
  why: 'The era has no great work to raise.',
};

/** How many workers turn out at the start of a round. */
export const workersDue = (civ) => (
  // Units are TRAINED by the Stewards with goods now, not drawn out of the
  // town's people, so nobody is "away". The old rule subtracted the band's
  // size from every dawn forever - an invisible standing cost that made
  // wanting a second piece self-harm, and that the playtest showed nothing
  // on any screen ever mentioned.
  (civ.happiness <= unhappyAt(civ) ? Math.floor(civ.population / 2) : civ.population) + (civ.extraWorkers || 0)
  // A town this glad turns out one more (after playtest 12).
  + (civ.happiness >= GLAD_WORKER_AT ? 1 : 0)
);
export const marketCoin = (civ) => 2 + civ.markets + modsOf(civ).marketCoin;
/** What a caravan brings back from a village: the load, plus what the Wheel and its kin add. */
// A CARAVAN BRINGS WHAT THE SETTLEMENT MAKES (Matt, playtest 11: 2 shifts
// and a coin for a flat 3 food AND a fed village was "way too good"). The
// load is the settlement's own day's yield (its size, more on a road, a
// town's standing order), plus what the Wheel and the like add. Without a
// settlement it is the old flat load, for anything that only reads the mods.
export const caravanLoad = (civ, v = null) => {
  const own = v ? (Object.values(villageYield(civ, v))[0] || 0) : VILLAGE.caravanLoad;
  return own + modsOf(civ).caravanGoods;
};
/** Growing a settlement is fed, not free: food goes out with the caravan while it can still grow. */
export const caravanFood = (civ, v) => (v && !v.chartered && v.size < VILLAGE.maxSize ? VILLAGE.caravanFood : 0);
/** A staffed field grows this much; the mill adds one per field. */
export const fieldFood = (civ) => Math.max(1, 2 + modsOf(civ).huntBase + (cityCount(civ, 'mill') > 0 ? 1 : 0) + modsOf(civ).fieldMore - modsOf(civ).fieldLess);
export const workshopGoods = () => 2;
// 4 coin, not 3 (after playtest 12, Matt: "the market turning two goods into
// three gold felt like a total waste of time" - it was a quarter short of par,
// docs/ECONOMY.md). Even from the start; Currency and the rest are profit.
export const marketSale = (civ) => ({ goods: 2, coin: 4 + modsOf(civ).marketCoin });
export const festivalJoy = (civ) => 2 + modsOf(civ).festivalJoy;
export const levyJoy = (civ) => Math.min(0, LEVY.happiness + modsOf(civ).levyHappiness);
/**
 * THE BONUSES RIDING ON A PLAY (playtest 14): the counselor's one liking,
 * when the card carries their tag, and the card's one pair-up, when a card
 * with its `with` tag was already played today. Each is a small object of
 * the same shape (happiness/knowledge/food/goods/coin, draw, hands, toBand,
 * compass, reveal, stone) with `text` for the board.
 */
export const bonusesFor = (civ, id, counselor = null) => {
  const out = [];
  const liked = likingFor(civ, id, counselor);
  if (liked) out.push({ ...COUNSELORS[liked].likes, who: liked });
  const w = pairUpFor(civ, id);
  if (w) out.push({ ...w, pair: true });
  return out;
};
const PATCH_KEYS = ['happiness', 'knowledge', 'food', 'goods', 'coin'];
/** What a Hall card costs to play: nothing, since playtest 14. The shelf's price is the whole price. */
export const cardCost = () => ({});
/** What a Hall card does when played, after the government, the laws, the liking and the pair-up have their say. */
export const cardEffect = (civ, id, counselor = null) => {
  const c = HALL_CARDS[id];
  if (!c) return {};
  // A grievance settles and gives nothing (Matt, playtest 14: "it needs to
  // be an annoyance, but one that is manageable") - unless a counselor
  // likes grievances, which is the Widow's whole point.
  const m = modsOf(civ);
  const out = c.grievance ? {} : { ...c.effect };
  const tags = c.tags || [];
  // The Calendar, Drama and the rest: a feast, and every song, gives more.
  if ((tags.includes('feast') || tags.includes('song')) && out.happiness > 0) out.happiness += m.festivalJoy;
  // Rituals, and the Year of the Old Songs: every rite or song the Hall plays is +1 happiness.
  if ((tags.includes('song') || tags.includes('rite')) && m.hallJoy) out.happiness = (out.happiness || 0) + m.hallJoy;
  // Philosophy and Theatre: a rally gives more.
  if (id === 'rally' && out.happiness > 0) out.happiness += m.rally;
  if (id === 'levy' && out.happiness < 0) out.happiness = Math.min(0, out.happiness + m.levyHappiness);
  // A crowd grumbles louder (Matt, after playtest 11): a `crowd` card that
  // already costs happiness costs one more per CROWD.per people past CROWD.from.
  if (c.crowd && out.happiness < 0) out.happiness -= crowdExtra(civ);
  if (c.ifTradition && (civ.hall?.compass?.x ?? 0) <= -2) Object.entries(c.ifTradition).forEach(([k, v]) => { out[k] = (out[k] || 0) + v; });
  bonusesFor(civ, id, counselor).forEach((b) => PATCH_KEYS.forEach((k) => { if (b[k]) out[k] = (out[k] || 0) + b[k]; }));
  return out;
};
/** Workers a card raises today, with the government's and the liking's extra. */
export const cardGives = (civ, id, counselor = null) => {
  const base = (HALL_CARDS[id]?.giveHands || 0) + (id === 'levy' || id === 'edict_of_hands' ? modsOf(civ).levyWorkers : 0);
  if (!base) return 0;
  return base + bonusesFor(civ, id, counselor).reduce((n, b) => n + (b.hands || 0), 0);
};
/** Cards a play draws: the card's own, plus a liking's. */
export const cardDraws = (civ, id, counselor = null) => (HALL_CARDS[id]?.draw || 0) + bonusesFor(civ, id, counselor).reduce((n, b) => n + (b.draw || 0), 0);
/** Do the workers this play raises all go to the Frontier (as orders)? */
export const sendsToBand = (civ, id, counselor = null) => Boolean(HALL_CARDS[id]?.toBand) || bonusesFor(civ, id, counselor).some((b) => b.toBand);
/** The compass push of a card, with a liking's. An OLD counselor has no pull left. */
export const cardPush = (id, counselor = null, civ = null, round = 0) => {
  const c = { ...(HALL_CARDS[id]?.compass || {}) };
  const liked = civ ? likingFor(civ, id, counselor) : null;
  const k = liked && COUNSELORS[liked]?.likes?.compass;
  const old = civ && liked ? isOld(civ, liked, round) : false;
  if (k && !old) { c.x = (c.x || 0) + (k.x || 0); c.y = (c.y || 0) + (k.y || 0); }
  return c;
};
export const studyKnowledge = (civ) => 1 + civ.libraries + modsOf(civ).study;
/** What a technology costs in knowledge, after the government has its say. */
export const techPrice = (civ, id) => Math.max(1, techCost(id) + modsOf(civ).techCost);
/** Coin to parley with what a unit stands beside: a camp's strength, or a rival's power, times the rate. Diplomacy halves it. */
export const parleyCost = (civ, { unit, foe } = {}) => {
  const u = unit ? unitById(civ, unit) : null;
  const camp = u ? foesNear(civ, u).find((f) => f.foe === 'camp' && f.id === foe) : null;
  const rival = foe ? rivalById(civ, foe) : null;
  const base = camp ? camp.strength * PARLEY_PER_STRENGTH : (rival ? rival.power * RIVAL.parleyPerPower : PARLEY_PER_STRENGTH);
  // Diplomacy halves it; so does a people of traders (the Salt Clans).
  const half = modsOf(civ).parleyHalf || (rival && !camp && wayOf(rival).parleyHalf);
  return Math.max(1, half ? Math.ceil(base / 2) : base);
};
export const huntBase = (civ) => HUNT_DIE.base + modsOf(civ).huntBase;

/** What a day's work would be where a unit stands, if anything. */
export const workHere = (civ, unit = null) => {
  const u = unit || unitsOf(civ)[0];
  return u ? workAt(civ, u.q, u.r) : null;
};

// A step is one hex, always. What varies is the PRICE of the hex, and
// Roads (which used to buy a second hex) now buys rough ground back down.
export const marchEase = (civ) => modsOf(civ).marchRange;
/** Workers to walk into this hex, with the roads the commonwealth has. */
export const stepInto = (civ, q, r) => stepCost(civ, q, r, marchEase(civ));
/** The word for a group this size: one is a scout, six is a host. */
/**
 * What the SAME action costs extra today: nothing the first time, one more
 * worker every time after. Keeps a day from being one button pressed four
 * times without capping anything outright.
 */
export const againSurcharge = (state, role, actionId) => REPEAT_STEP * (state?.taken?.[role]?.[actionId] || 0);

export const sightOf = (civ) => BAND.sight + modsOf(civ).sight;
export const hitOn = (civ) => Math.max(2, BAND.hitOn + modsOf(civ).hitOn);
/** Dice a unit brings to a day's work on the ground it stands on. */
export const huntDice = (civ) => Math.max(1, 1 + modsOf(civ).huntDice - modsOf(civ).huntLess);
/**
 * What a hex is CALLED: the town, a region once it has risen, unknown
 * ground before that, otherwise what sort of country it is. Every line that
 * used to print "3,-4" goes through here.
 */
export const placeName = (civ, q, r) => {
  const site = siteAt(q, r, civ);
  if (site >= 0 && !isTown(q, r)) {
    const regionId = sitesOf(civ)[site];
    const found = regionId && (civ.regions || []).includes(regionId);
    if (found) return REGIONS[regionId].name;
  }
  return groundAt(civ, q, r).name;
};

/** What clearing a camp pays: coin equal to its strength, and no table. */
export const lootOf = (m) => ({ coin: Math.max(1, m?.strength || 1) });

// ------------------------------------------------------ villages and towns
// A town another people holds is theirs until won back: it sends nothing,
// musters nobody and is not counted here. `takenTownAt` in units.js finds it.
export const villagesOf = (civ) => (civ?.villages || []).filter((v) => !v.taken);
export const villageAt = (civ, q, r) => villagesOf(civ).find((v) => v.q === q && v.r === r) || null;
export const damagedAt = (civ, q, r) => {
  const v = villageAt(civ, q, r);
  return v && v.damaged ? v : null;
};
/** What a village on this ground sends up: whatever the ground makes. */
export const villageYieldAt = (civ, q = null, r = null) => {
  const u = q === null ? firstFounder(civ) : { q, r };
  if (!u) return null;
  return workAt(civ, u.q, u.r);
};
const firstFounder = (civ) => unitsOf(civ).find((x) => kindOf(x).canFound) || null;
/**
 * Where a village may be planted: workable wild ground, outside the city,
 * not on a camp, and not crowded up against the city or another village.
 */
export const canFoundAt = (civ, q, r) => {
  if (isTown(q, r)) return false;
  if (!canStand(q, r, civ)) return false;
  if (!workAt(civ, q, r)) return false;
  if ((civ.menaces || []).some((m) => m.q === q && m.r === r)) return false;
  if (villageAt(civ, q, r)) return false;
  if (hexDistance(q, r) < VILLAGE.spacing + TOWN_RADIUS - 1) return false;
  return !villagesOf(civ).some((v) => hexDistanceBetween([v.q, v.r], [q, r]) < VILLAGE.spacing);
};
/** A village trickles its size in whatever the ground makes; a town fills its order. */
/** Can this town be raised to a city: size, its own patch, and settlers to spare? */
export const canRaiseCity = (civ, v) => {
  if (!v || !v.chartered || v.city || v.damaged || v.size < CITY.fromSize) return false;
  if ((civ.population || 0) < CITY.settlersFrom) return false;
  const near = (q, r) => hexDistanceBetween([q, r], [v.q, v.r]) <= CITY.patch;
  if (villagesOf(civ).some((x) => x.id !== v.id && near(x.q, x.r))) return false;
  if ([...rivalGround(civ).keys()].some((k) => near(...k.split(',').map(Number)))) return false;
  const around = [[1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1]].filter(([dq, dr]) => workAt(civ, v.q + dq, v.r + dr)).length;
  return around >= CITY.landAround;
};
export const villageYield = (civ, v) => {
  if (v.damaged) return {};
  if (v.city) {
    // A city makes its size in its focus (defence makes none: it holds the
    // ground), and its district adds a little of its own.
    const out = { ...(CITY.districts[v.district]?.night || {}) };
    if (v.focus && v.focus !== 'defence') out[v.focus] = (out[v.focus] || 0) + v.size + roadBonus(civ, v);
    return out;
  }
  if (v.chartered) {
    const kind = TOWN.orders.includes(v.order) ? v.order : 'food';
    return { [kind]: v.size * TOWN.yieldPerSize + roadBonus(civ, v) };
  }
  const w = workAt(civ, v.q, v.r);
  // A village joined to the city by road sends the road's level more.
  return w ? { [w.resource]: v.size + roadBonus(civ, v) } : {};
};
/** How strong a warband may be trained or reinforced to, with the tree. */
export const warbandCap = (civ) => WARBAND_CAP + modsOf(civ).bandMax;
/**
 * Where a unit appears when it is trained: the city, or any town. A town is
 * a FORWARD BASE as well as an income, so expansion buys reach - a warband
 * does not have to walk the length of the island to get to work.
 */
export const musterPoints = (civ) => [
  { id: 'city', name: 'the city', q: 0, r: 0 },
  ...villagesOf(civ).filter((v) => v.chartered).map((v) => ({
    id: v.id, name: v.name, q: v.q, r: v.r,
  })),
];
export const atMuster = (civ, q, r) => musterPoints(civ).some((m) => m.q === q && m.r === r);
/** A scout needs Tracking; the rest the town has always known how to raise. */
export const canTrain = (civ, kind) => {
  if (!UNIT_KINDS[kind]) return false;
  // A scout needs Tracking; the rest the town has always known how to
  // raise. Units stack at a muster point, so one standing at home never
  // blocks the next.
  if (kind === 'scout' && !(civ.techs || []).includes('tracking')) return false;
  return musterPoints(civ).length > 0;
};
export const canReinforce = (civ, u) => Boolean(u) && u.kind === 'warband'
  && atMuster(civ, u.q, u.r) && u.strength < warbandCap(civ);

export const unhappyAt = (civ) => UNHAPPY_AT + modsOf(civ).unhappyAt;
export const cheapestResearch = (civ) => Math.min(...availableTechs(civ).map(techCost), 3);
/** Experiments a day: one, plus one per library. */
/**
 * The experiments a day gives you at a worker each: one, plus one per
 * library. You may always run MORE - they just cost more people, 2 then 3
 * then 4. Matt, 2026-09-19: "the scholars can more or less only use one
 * worker" - so spare people buy more of the deduction rather than nothing.
 */
/** Every principle the Scholars may work with. */
export const benchPrinciples = () => PRINCIPLES;
/** A deep technology the shelf already holds the makings of. */
export const makeable = (civ, id) => Boolean(TECHS[id]) && !onBench(TECHS[id].tier) && techReachable(civ, id)
  && Boolean(machineForTier(TECHS[id].tier)) && Boolean(fittingProduct(civ, TECHS[id].tier, civ?.lore?.reactions?.[id]));
export const mixesPerDay = (civ) => 1 + (civ.libraries || 0) + modsOf(civ).mixes;
export const mixesLeft = (state) => mixesPerDay(state.civ) - ((state.taken?.science?.mix) || 0);
/** What the NEXT experiment today costs, in workers. */
export const mixWorkers = (state) => {
  // A firing is an experiment too: they share the day's allowance.
  const done = (state?.taken?.science?.mix || 0) + (state?.taken?.science?.fire || 0);
  const free = mixesPerDay(state?.civ || {});
  return done < free ? 1 : 2 + (done - free);
};
export const mixCost = (state) => ({ workers: mixWorkers(state) });
export const natureFromKey = (k) => NATURES.find((n) => natureKey(n) === k) || null;

export const resolveCost = (action, civ, times = 1, params = {}, state = null) => {
  const base = typeof action.cost === 'function' ? action.cost(civ, params, state) : action.cost || {};
  if (times === 1) return base;
  return Object.fromEntries(Object.entries(base).map(([k, n]) => [k, n * times]));
};

/** The infrastructure cap on repeating this action (1 if it can't repeat). */
export const capacity = (action, civ) => (
  action?.repeat ? Math.max(1, Math.min(MAX_TIMES, action.repeat(civ))) : 1
);

/** How many times this action could be taken right now: the cap, or what is affordable, whichever is less. */
export const maxTimes = (state, role, actionId) => {
  const action = findAction(role, actionId);
  const civ = seenBy(state, role);
  const cap = capacity(action, civ);
  let n = 1;
  while (n < cap && canAfford(civ, resolveCost(action, civ, n + 1, {}, state))) n += 1;
  return n;
};
export const resolveLabel = (action, civ) => (typeof action.label === 'function' ? action.label(civ) : action.label);

export const actionsForRole = (role) => [...(ROLE_ACTIONS[role] || []), MONUMENT_ACTION];

export const findAction = (role, actionId) => actionsForRole(role).find((a) => a.id === actionId) || null;

/**
 * Is this action playable right now? Returns { ok, why } so the phone can
 * grey a button AND say what is missing - "1 more worker" is the whole
 * turn-order puzzle made visible.
 */
// During the day a role reads `workers` as ITS hands (game.js civFor); the
// helper is inlined here to keep roleActions free of the reducer.
const seenBy = (state, role) => (
  state.meta?.phase === 'day' && state.hands ? { ...state.civ, workers: state.hands[role] ?? 0 } : state.civ
);

export const checkAction = (state, role, actionId, params = {}) => {
  const action = findAction(role, actionId);
  if (!action) return { ok: false, why: 'No such action.' };
  const civ = seenBy(state, role);
  // Once a day, per action: the day is simultaneous and has no turns, so
  // this is what keeps a free action from being pressed forever.
  // `kind` says WHY not, so a board can tell "the ground forbids it" from
  // "you have not got the workers" - the first is worth hiding, the second
  // is worth greying, because its price is already on the tile.
  if (state.taken?.[role]?.[actionId] && !action.again) return { ok: false, kind: 'taken', why: 'Already done today.' };
  // Some repeatable things have a daily limit set by infrastructure: one
  // caravan per market, one hire per market.
  if (action.limit && (state.taken?.[role]?.[actionId] || 0) >= action.limit(civ)) {
    return { ok: false, kind: 'taken', why: `Only ${action.limit(civ)} a day - build more ${action.limitText || ''}.`.replace(/ \.$/, '.') };
  }
  if (action.legal && !action.legal(state)) return { ok: false, kind: 'rule', why: action.why };
  const times = Number.isInteger(params.times) ? params.times : 1;
  if (times < 1 || times > capacity(action, civ)) return { ok: false, kind: 'rule', why: `Only ${capacity(action, civ)} at a time - build more ${action.capText || ''}.`.replace(/ \.$/, '.') };
  const cost = resolveCost(action, civ, times, params, state);
  if (!canAfford(civ, cost)) return { ok: false, kind: 'cost', why: `Needs ${missingFor(civ, cost).join(' and ')}.` };
  if (action.needsParam && params[action.needsParam] === undefined) {
    return { ok: true, why: null, needsParam: action.needsParam };
  }
  if (action.legalParam && !action.legalParam(civ, params[action.needsParam], params)) {
    return { ok: false, kind: 'rule', why: action.whyParam || 'Already known.' };
  }
  return { ok: true, why: null };
};

/** Every tech whose prerequisites are known and that isn't known yet. */
export const availableTechs = (civ) => Object.keys(TECHS).filter((id) => techReachable(civ, id));

export const regionInfo = (id) => REGIONS[id];

/** Directions with land still to find, as DIRS indices. */
export const unexploredDirections = (civ) => {
  const sites = sitesOf(civ);
  const found = new Set(civ.regions || []);
  return allSites(civ).map((_, i) => i).filter((i) => sites[i] && !found.has(sites[i]));
};

/** Warbands this one could merge into: on its hex or beside it, room under the cap for both. */
export const mergeTargets = (civ, u) => {
  if (!u || (u.kind || 'warband') !== 'warband') return [];
  return unitsOf(civ).filter((t) => t.id !== u.id && (t.kind || 'warband') === 'warband'
    && Math.max(Math.abs(t.q - u.q), Math.abs(t.r - u.r), Math.abs((t.q + t.r) - (u.q + u.r))) <= 1
    && t.strength + u.strength <= WARBAND_CAP);
};

/** What the Stewards can upgrade, and to what (the Bronze Age). */
export const UPGRADES = { workshop: 'Foundry', field: 'Orchard' };
/** Can this unit raise a fort where it stands: a warband, on open ground with none there. */
export const canFortAt = (civ, u) => Boolean(u) && (u.kind || 'warband') === 'warband' && !isTown(u.q, u.r)
  && siteAt(u.q, u.r, civ) < 0 && !villagesOf(civ).some((v) => v.q === u.q && v.r === u.r)
  && !(civ.forts || []).includes(`${u.q},${u.r}`);
/** The nearest far region still out in the fog, for a survey. */
export const surveyTarget = (civ) => {
  const seen = new Set(civ.seen || []);
  return (civ.farSites || [])
    .map((hexes, i) => ({ hexes, i }))
    .filter(({ hexes }) => !hexes.every(([q, r]) => seen.has(`${q},${r}`)))
    .sort((a, b) => hexDistance(...a.hexes[0]) - hexDistance(...b.hexes[0]))[0] || null;
};
